<script>
// Conectar o WhatsApp do profissional à IA (canal WhatsApp do AAWZ Intelligence).
//
// Inbound-first: a plataforma NÃO pede o número nem envia nada. Ela mostra um
// código `AAWZ-NNNN` (válido por 10 min) e o profissional ENVIA esse código ao
// número da IA — pelo botão "Abrir WhatsApp" (link wa.me, que abre o app no
// celular e o WhatsApp Web/Desktop no computador) ou digitando. O webhook do
// backend casa o código e ativa o vínculo; enquanto isso este modal consulta o
// estado a cada 3 s até virar `ativo`. Por que assim: a posse do número é
// provada pelo próprio WhatsApp (o remetente não é forjável), não existe
// "digitei errado", e nunca iniciamos conversa com número desconhecido — o
// principal sinal de spam para um número de WhatsApp.
import Vue from 'vue'
import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import { WHATSAPP_CONECTAR_TEXT } from '@modules/aawz-intelligence-v2/constants/Text'
import { apiWhatsappVinculo } from '@modules/aawz-intelligence-v2/api/apiWhatsappVinculo'
import { formatPhone } from '@utils/Format'
import Title from '@components/section-title'
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'
import LinhaDeToggle from '@modules/aawz-intelligence-v2/components/linha-de-toggle/linha-de-toggle.vue'

export const INTERVALO_POLLING_MS = WHATSAPP_CONECTAR_TEXT.intervaloPollingMs
const STATUS_NENHUM = 'nenhum'
const STATUS_PENDENTE = 'pendente'
const STATUS_ATIVO = 'ativo'
const HTTP_SEM_ACESSO = 403
const HTTP_OK = 200
const HTTP_CONFLITO = 409
const ONBOARDING_NENHUM = 'nenhum'
const ONBOARDING_EM_ANDAMENTO = 'em_andamento'
const ONBOARDING_CONCLUIDO = 'concluido'

const RE_DATA_HORA_BACKEND = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/

export default {
  name: 'WhatsappConectar',
  components: { Title, FeedbackWrapper, LinhaDeToggle },
  data() {
    return {
      busModal: new Vue(),
      vinculo: { status: STATUS_NENHUM },
      carregando: false,
      gerando: false,
      revogando: false,
      iniciandoOnboarding: false,
      // chave da preferência = true enquanto o PATCH está em voo
      salvando: {},
      semAcesso: false,
      // Motivo do 403 como o backend mandou (`gate.MENSAGEM_SEM_ACESSO`): fonte única.
      mensagemSemAcesso: '',
      // Fecha pelo X/Esc do BaseModal (`close` no bus) ou destrói vira false; uma
      // resposta em voo do polling não pode religá-lo depois disso.
      aberto: false,
      pollingId: null,
      WHATSAPP_CONECTAR_TEXT,
      exemplos: WHATSAPP_CONECTAR_TEXT.exemplosDePerguntas,
    }
  },
  computed: {
    estaPendente() {
      return this.vinculo.status === STATUS_PENDENTE
    },
    estaAtivo() {
      return this.vinculo.status === STATUS_ATIVO
    },
    // Só com a flag do escritório (`pode_onboarding`, lida pelo backend do gate).
    mostraOnboarding() {
      return this.estaAtivo && Boolean(this.vinculo.pode_onboarding)
    },
    onboardingStatus() {
      const onboarding = this.vinculo.onboarding || {}
      return onboarding.status || ONBOARDING_NENHUM
    },
    podeIniciarOnboarding() {
      return this.mostraOnboarding && this.onboardingStatus === ONBOARDING_NENHUM
    },
    // Texto do chip de estado; vazio quando ainda não iniciou (mostra o botão).
    onboardingStatusTexto() {
      const textos = WHATSAPP_CONECTAR_TEXT.onboarding
      if (this.onboardingStatus === ONBOARDING_EM_ANDAMENTO) {
        return textos.statusEmAndamento
      }
      if (this.onboardingStatus === ONBOARDING_CONCLUIDO) {
        const onboarding = this.vinculo.onboarding || {}
        return onboarding.em_fila ? textos.statusFila : textos.statusConcluido
      }
      return ''
    },
    // `*-text` são os tons de TEXTO das cores de status (`_controls.sass`): em
    // modo `text` a cor vira texto/bolinha, e `--warning` puro (#FF6433,
    // laranja-avermelhado) leria como erro num "em andamento".
    onboardingStatusCor() {
      return this.onboardingStatus === ONBOARDING_CONCLUIDO
        ? 'success-text'
        : 'warning-text'
    },
    // Pendente com código utilizável — o backend devolve `codigo: null` quando
    // já expirou, e é isso (não um relógio local) que decide a tela.
    codigoVigente() {
      return (
        this.estaPendente && !this.vinculo.expirado && !!this.vinculo.codigo
      )
    },
    codigoExpirado() {
      return (
        this.estaPendente && (this.vinculo.expirado || !this.vinculo.codigo)
      )
    },
    verificadoEmFormatado() {
      const val = this.vinculo.verificado_em
      if (!val) return ''
      // O backend já serializa 'DD/MM/YYYY HH:mm' (day-first). Não fazer round-trip
      // por aawzDate: `format` lê o dia em UTC e a hora em local, e em BRT qualquer
      // hora >= 21:00 viraria o dia seguinte (mesmo caso do PR 1799 do frontend).
      const texto = String(val)
      return RE_DATA_HORA_BACKEND.test(texto) ? texto : ''
    },
    // `formatPhone` (@utils/Format) já cobre 10–13 dígitos com/sem DDI.
    numeroIaFormatado() {
      const digitos = String(this.vinculo.numero_ia || '')
      return digitos ? formatPhone(digitos) : ''
    },
  },
  created() {
    GlobalBus.$on(MODAL.IA_WHATSAPP_CONECTAR, this.open)
    // O BaseModal avisa o fechamento (X, Esc, clique fora) com `close` no bus —
    // não com `dialog false`, que é só o comando de abrir/fechar que NÓS emitimos.
    this.busModal.$on('close', this.aoFechar)
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.IA_WHATSAPP_CONECTAR, this.open)
    this.busModal.$off('close', this.aoFechar)
    this.aoFechar()
  },
  methods: {
    // Reset do estado local — 1ª linha de open() (refactor-rules.md #17).
    clear() {
      this.pararPolling()
      this.vinculo = { status: STATUS_NENHUM }
      this.carregando = false
      this.gerando = false
      this.revogando = false
      this.iniciandoOnboarding = false
      this.semAcesso = false
    },
    open() {
      this.clear()
      this.aberto = true
      this.busModal.$emit('dialog', true)
      this.carregar()
    },
    aoFechar() {
      this.aberto = false
      this.pararPolling()
    },
    abrirWhatsapp() {
      // O BaseButton renderiza sempre button nativo (sem tag/href): abrir pelo JS,
      // como em billing-system-filters.vue.
      if (!this.vinculo.link_wa) return
      globalThis.open(this.vinculo.link_wa, '_blank', 'noopener,noreferrer')
    },

    async carregar() {
      this.carregando = true
      const r = await apiWhatsappVinculo.obter()
      this.carregando = false
      if (!this.aberto) return
      this.aplicarResposta(r)
    },

    // Devolve true quando a resposta trouxe um estado de vínculo.
    aplicarResposta(r) {
      if (r && r.status === HTTP_SEM_ACESSO) {
        this.semAcesso = true
        this.mensagemSemAcesso =
          r.message || WHATSAPP_CONECTAR_TEXT.semAcessoFallback
        this.pararPolling()
        return false
      }
      if (r && r.status === HTTP_OK && r.value) {
        this.vinculo = r.value
        this.ajustarPolling()
        return true
      }
      this.$showSnack(
        r || { text: 'Não foi possível carregar o estado do WhatsApp.' }
      )
      return false
    },

    async gerarCodigo() {
      this.gerando = true
      const r = await apiWhatsappVinculo.gerarCodigo()
      this.gerando = false
      if (r && r.status === HTTP_OK) {
        this.aplicarResposta(r)
        return
      }
      if (r && r.status === HTTP_CONFLITO && r.value) {
        this.vinculo = r.value
        this.ajustarPolling()
      }
      this.$showSnack(r || { text: 'Não foi possível gerar o código.' })
    },

    // Preferências (escrita v667, avisos v668+): otimista, com volta ao valor
    // anterior se o backend recusar — o `v-switch` não pode ficar "ligado" com o
    // servidor em off. `item` = entrada de `WHATSAPP_CONECTAR_TEXT` com `chave`.
    async alternarPreferencia(item, valor) {
      const chave = item.chave
      const anterior = Boolean(this.vinculo[chave])
      this.vinculo = { ...this.vinculo, [chave]: valor }
      this.salvando = { ...this.salvando, [chave]: true }
      const r = await apiWhatsappVinculo.definirPreferencias({ [chave]: valor })
      this.salvando = { ...this.salvando, [chave]: false }
      if (r && r.status === HTTP_OK && r.value) {
        this.vinculo = r.value
        this.$showSnack({
          text: valor ? item.snackLigada : item.snackDesligada,
          button: false,
        })
        return
      }
      this.vinculo = { ...this.vinculo, [chave]: anterior }
      this.$showSnack(
        r || { text: WHATSAPP_CONECTAR_TEXT.snackPreferenciaFalhou }
      )
    },
    alternarEscrita(habilitada) {
      return this.alternarPreferencia(WHATSAPP_CONECTAR_TEXT.escrita, habilitada)
    },

    confirmarOnboarding() {
      const textos = WHATSAPP_CONECTAR_TEXT.onboarding.confirmacao
      this.$showModal({
        type: 'warning',
        v2: true,
        center: true,
        title: textos.titulo,
        text: textos.texto,
        buttons: [
          { text: 'Cancelar', action: () => {} },
          { text: textos.botao, action: () => this.iniciarOnboarding() },
        ],
      })
    },

    async iniciarOnboarding() {
      this.iniciandoOnboarding = true
      const r = await apiWhatsappVinculo.iniciarOnboarding()
      this.iniciandoOnboarding = false
      if (!this.aberto) return
      // Em qualquer desfecho o backend devolve o estado atual do vínculo (o
      // 409 "já realizado", por exemplo, traz `onboarding.status = concluido`).
      if (r && r.value && r.value.status) {
        this.vinculo = r.value
        this.ajustarPolling()
      }
      if (r && r.status === HTTP_OK) {
        this.$showSnack({
          text: WHATSAPP_CONECTAR_TEXT.onboarding.snackIniciado,
          button: false,
        })
        return
      }
      this.$showSnack(r || { text: WHATSAPP_CONECTAR_TEXT.onboarding.snackFalhou })
      // 403 = a flag do escritório foi desligada nesta sessão: recarrega para o
      // botão sumir com o estado real, em vez de ficar visível pelo cache local.
      if (r && r.status === HTTP_SEM_ACESSO) this.carregar()
    },

    confirmarDesconexao() {
      this.$showModal({
        type: 'danger',
        v2: true,
        center: true,
        title: WHATSAPP_CONECTAR_TEXT.confirmarDesconexao.titulo,
        text: WHATSAPP_CONECTAR_TEXT.confirmarDesconexao.texto,
        buttons: [
          { text: 'Cancelar', action: () => {} },
          { text: 'Desconectar', action: () => this.revogar() },
        ],
      })
    },

    async revogar() {
      this.revogando = true
      const r = await apiWhatsappVinculo.revogar()
      this.revogando = false
      if (r && r.status === HTTP_OK) {
        this.vinculo =
          r.value && r.value.status ? r.value : { status: STATUS_NENHUM }
        this.pararPolling()
        this.$showSnack({
          text: WHATSAPP_CONECTAR_TEXT.snackDesconectado,
          button: false,
        })
        return
      }
      this.$showSnack(r || { text: 'Não foi possível desconectar.' })
    },

    // Polling só enquanto há código vigente esperando o profissional enviar.
    ajustarPolling() {
      if (this.codigoVigente && this.aberto) this.iniciarPolling()
      else this.pararPolling()
    },
    iniciarPolling() {
      if (this.pollingId || !this.aberto) return
      this.pollingId = setInterval(this.consultar, INTERVALO_POLLING_MS)
    },
    pararPolling() {
      if (!this.pollingId) return
      clearInterval(this.pollingId)
      this.pollingId = null
    },
    async consultar() {
      const r = await apiWhatsappVinculo.obter()
      // resposta em voo depois de fechar: não religa o polling nem mexe na tela
      if (!this.aberto) return
      if (r && r.status === HTTP_SEM_ACESSO) {
        this.aplicarResposta(r)
        return
      }
      if (r && r.status === HTTP_OK && r.value) {
        const virouAtivo = r.value.status === STATUS_ATIVO && !this.estaAtivo
        this.vinculo = r.value
        this.ajustarPolling()
        if (virouAtivo) {
          this.$showSnack({
            text: WHATSAPP_CONECTAR_TEXT.snackConectado,
            button: false,
          })
        }
      }
    },
  },
}
</script>

<template>
  <BaseModal max-width="45%" :bus="busModal" fixedActions>
    <template v-slot:title>
      <Title :title="WHATSAPP_CONECTAR_TEXT.titulo" underlined />
    </template>

    <div class="pa-1 mb-4">
      <FeedbackWrapper :loading="carregando" :min-height="160">
        <!-- Sem acesso: escritório sem a funcionalidade -->
        <div v-if="semAcesso" data-test="wa-sem-acesso">
          <p class="az-text-secondary az-size-14 mb-0">
            {{ mensagemSemAcesso }}
          </p>
        </div>

        <!-- Conectado -->
        <div v-else-if="estaAtivo" data-test="wa-ativo">
          <div class="d-flex align-center az-gap-small mb-3 wa-ok">
            <icon-svg name="mdi-whatsapp" size="20" class="wa-ok__icon" />
            <span class="az-text-primary az-size-14 font-semibold">
              WhatsApp conectado
            </span>
          </div>
          <p class="az-text-secondary az-size-14 mb-1">
            Número <strong>{{ vinculo.numero_mascarado || '—' }}</strong>
            <span v-if="verificadoEmFormatado">
              · desde {{ verificadoEmFormatado }}</span
            >
          </p>
          <p class="az-text-secondary az-size-14 mb-3">
            Pergunte por lá o mesmo que perguntaria aqui. Alguns exemplos:
          </p>
          <ul class="wa-exemplos mb-4">
            <li
              v-for="exemplo in exemplos"
              :key="exemplo"
              class="az-text-primary az-size-14"
            >
              {{ exemplo }}
            </li>
          </ul>
          <LinhaDeToggle
            class="mb-4"
            :rotulo="WHATSAPP_CONECTAR_TEXT.escrita.rotulo"
            :ajuda="WHATSAPP_CONECTAR_TEXT.escrita.ajuda"
            :value="Boolean(vinculo.escrita_habilitada)"
            :disabled="Boolean(salvando.escrita_habilitada)"
            data-test="wa-escrita"
            @change="alternarEscrita"
          />
          <p class="az-text-primary az-size-14 font-semibold mb-2">
            {{ WHATSAPP_CONECTAR_TEXT.avisos.titulo }}
          </p>
          <LinhaDeToggle
            v-for="item in WHATSAPP_CONECTAR_TEXT.avisos.itens"
            :key="item.chave"
            class="mb-4"
            :rotulo="item.rotulo"
            :ajuda="item.ajuda"
            :value="Boolean(vinculo[item.chave])"
            :disabled="Boolean(salvando[item.chave])"
            :data-test="`wa-pref-${item.chave}`"
            @change="alternarPreferencia(item, $event)"
          />
          <div v-if="mostraOnboarding" class="mb-4" data-test="wa-onboarding">
            <p class="az-text-primary az-size-14 font-semibold mb-1">
              {{ WHATSAPP_CONECTAR_TEXT.onboarding.titulo }}
            </p>
            <p class="az-text-secondary az-size-14 mb-2">
              {{ WHATSAPP_CONECTAR_TEXT.onboarding.ajuda }}
            </p>
            <BaseButton
              v-if="podeIniciarOnboarding"
              primary
              fitContent
              :loading="iniciandoOnboarding"
              data-test="wa-onboarding-iniciar"
              @click="confirmarOnboarding"
            >
              {{ WHATSAPP_CONECTAR_TEXT.onboarding.botao }}
            </BaseButton>
            <BaseStatus
              v-else-if="onboardingStatusTexto"
              text
              preserve-case
              :content="onboardingStatusTexto"
              :color="onboardingStatusCor"
              data-test="wa-onboarding-status"
            />
          </div>
          <BaseButton
            outline
            fitContent
            :loading="revogando"
            @click="confirmarDesconexao"
          >
            Desconectar
          </BaseButton>
        </div>

        <!-- Código vigente: enviar pelo WhatsApp -->
        <div v-else-if="codigoVigente" data-test="wa-pendente">
          <p class="az-text-secondary az-size-14 mb-3">
            Envie o código abaixo para o WhatsApp da AAWZ Intelligence. Assim
            que a mensagem chegar, seu número fica conectado a esta conta.
          </p>
          <div class="wa-codigo ia-texto-titulo-cartao pa-4 text-center mb-3" data-test="wa-codigo">{{
            vinculo.codigo
          }}</div>
          <div class="d-flex align-center az-gap-small mb-3">
            <BaseButton
              v-if="vinculo.link_wa"
              primary
              fitContent
              data-test="wa-abrir"
              @click="abrirWhatsapp"
            >
              <icon-svg name="mdi-whatsapp" size="16" class="mr-1" />
              Abrir WhatsApp com o código
            </BaseButton>
            <BaseButton
              outline
              fitContent
              :loading="gerando"
              @click="gerarCodigo"
            >
              Gerar outro código
            </BaseButton>
          </div>
          <p class="az-text-secondary az-size-12 mb-1">
            Ou mande a mensagem "{{ vinculo.codigo }}" para
            <strong>{{
              numeroIaFormatado || 'o número da AAWZ Intelligence'
            }}</strong
            >. O código vale por 10 minutos.
          </p>
          <p
            class="az-text-secondary az-size-12 mb-0 d-flex align-center az-gap-small"
          >
            <v-progress-circular indeterminate size="14" width="2" />
            Aguardando sua mensagem…
          </p>
        </div>

        <!-- Código expirado -->
        <div v-else-if="codigoExpirado" data-test="wa-expirado">
          <p class="az-text-secondary az-size-14 mb-3">
            O código expirou antes de ser enviado. Gere outro e envie em até 10
            minutos.
          </p>
          <BaseButton
            primary
            fitContent
            :loading="gerando"
            @click="gerarCodigo"
          >
            Gerar novo código
          </BaseButton>
        </div>

        <!-- Sem vínculo: explicar e gerar -->
        <div v-else data-test="wa-nenhum">
          <p class="az-text-secondary az-size-14 mb-2">
            Converse com a AAWZ Intelligence pelo WhatsApp: patrimônio de um
            cliente, suas reuniões e tarefas do dia, o que foi decidido na
            última reunião.
          </p>
          <p class="az-text-secondary az-size-12 mb-3">
            As respostas podem conter dados dos seus clientes e serão enviadas
            ao WhatsApp que você conectar. Você pode desconectar a qualquer
            momento.
          </p>
          <BaseButton
            primary
            fitContent
            :loading="gerando"
            @click="gerarCodigo"
          >
            <icon-svg name="mdi-whatsapp" size="16" class="mr-1" />
            Gerar código de conexão
          </BaseButton>
        </div>
      </FeedbackWrapper>
    </div>

    <template v-slot:actions="{ close }">
      <!-- mt-4 no container: margem direta em BaseButton é proibida. -->
      <div class="d-flex align-center justify-end mt-4">
        <BaseButton underlined @click="close">Fechar</BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<style lang="sass" scoped>
// Código de ativação: precisa ser lido e copiado sem erro, por isso monoespaçado,
// grande e com espaçamento entre dígitos — não há utilitário Vuetify para isso.
// `user-select: all` faz um clique selecionar o código inteiro.
// Padding e alinhamento ficam nas classes utilitárias (`pa-4`, `text-center`);
// `border-radius` fica aqui porque este bundle importa o Vuetify treeshaken sem
// as utilities `.rounded-*`.
.wa-codigo
  color: var(--neutral-900)
  letter-spacing: 0.12em
  border-radius: 8px
  background: var(--neutral-100)
  user-select: all

.wa-ok__icon
  color: var(--aawz-green-color)

.wa-exemplos
  // o reset global tira o marcador de toda lista; aqui a lista e de exemplos
  list-style: disc
  padding-left: 20px
  display: grid
  gap: 4px
</style>
